// javascript
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import ArticleIcon from '@mui/icons-material/Article';
import cambioEstadoService from '../services/cambioEstadoService';
import { subirArchivo } from '../services/archivoService';

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
};

function GestorEstadoEsperaDictamenTribunal({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [historialEstados, setHistorialEstados] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [infoPericia, setInfoPericia] = useState(null);
    const [infoAbogado, setInfoAbogado] = useState(null);

    // Controla qué formulario se muestra: 'dictamen', 'sentencia' o null
    const [formularioActivo, setFormularioActivo] = useState(null);

    // Estado del formulario de Dictamen (Intermedio)
    const [datosDictamen, setDatosDictamen] = useState({
        fecha: obtenerFechaActual(),
        observaciones: ''
    });

    // Estados del formulario de Sentencia (Final)
    const [datosSentencia, setDatosSentencia] = useState({
        resultado: '', // Favorable o Desfavorable
        fecha: obtenerFechaActual(),
        observaciones: ''
    });
    const [archivos, setArchivos] = useState([]);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        cargarHistorial();
    }, [tramite.idTramite]);

    const cargarHistorial = async () => {
        setCargandoHistorial(true);
        try {
            const historial = await cambioEstadoService.getHistorialEstados(tramite.idTramite);
            setHistorialEstados(historial);

            // 1. Buscar info de la pericia realizada (Estado 17)
            const cambioPericiaRealizada = historial
                .filter(c => c.comentario && c.comentario.includes('Pericia Médica Realizada'))
                .pop();

            if (cambioPericiaRealizada && cambioPericiaRealizada.comentario) {
                parsearInfoPericia(cambioPericiaRealizada.comentario);
            }

            // 2. Buscar info del abogado responsable (Estado 16) [cite: 97, 61]
            const cambioPresentacionDoc = historial
                .filter(c => c.comentario && c.comentario.includes('Presentación de documentación registrada'))
                .pop();

            if (cambioPresentacionDoc && cambioPresentacionDoc.comentario) {
                const abogadoMatch = cambioPresentacionDoc.comentario.match(/Abogado responsable: (.+?)(?:\n|$)/);
                if (abogadoMatch && !abogadoMatch[1].includes('No especificado')) {
                    setInfoAbogado(abogadoMatch[1].trim());
                }
            }

        } catch (error) {
            console.error("Error al cargar historial:", error);
            toast.error("No se pudo cargar la información de la pericia.");
        } finally {
            setCargandoHistorial(false);
        }
    };

    const parsearInfoPericia = (comentario) => {
        const info = {
            fechaRealizacion: null,
            observaciones: null
        };

        const fechaMatch = comentario.match(/Fecha de realización: (.+?)(?:\n|$)/);
        if (fechaMatch) info.fechaRealizacion = fechaMatch[1].trim();

        const obsMatch = comentario.match(/Observaciones: (.+?)(?:\n|$)/);
        if (obsMatch && !obsMatch[1].includes('Sin observaciones')) info.observaciones = obsMatch[1].trim();

        setInfoPericia(info);
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return '';
        const fecha = new Date(fechaString);
        if (isNaN(fecha.getTime())) return '';
        const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
        return fechaLocal.toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // --- MANEJO DE ARCHIVOS ---
    const handleSeleccionarArchivos = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        const nuevosArchivos = files.map(file => ({
            id: `local_${Date.now()}_${Math.random()}`,
            file,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            estado: "pendiente",
            esDelBackend: false
        }));

        setArchivos(prev => [...prev, ...nuevosArchivos]);
        if (event.target) event.target.value = "";
    };

    const handleDrag = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleEliminarArchivo = (id) => {
        setArchivos(prev => prev.filter(a => a.id !== id));
    };

    // --- SUBIDA COMÚN ---
    const subirArchivosPendientes = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) return [];

        setSubiendoArchivos(true);
        const archivosSubidos = [];
        for (const archivo of archivosPendientes) {
            try {
                const archivoSubido = await subirArchivo(tramite.idTramite, archivo.file);
                archivosSubidos.push(archivoSubido);
                setArchivos(prev => prev.map(item =>
                    item.id === archivo.id ? { ...item, estado: "subido", idArchivo: archivoSubido.idArchivo, esDelBackend: true } : item
                ));
            } catch (error) {
                toast.error(`Error subiendo ${archivo.nombre}`);
                throw error;
            }
        }
        setSubiendoArchivos(false);
        return archivosSubidos;
    };

    // --- ACCIÓN 1: GUARDAR DICTAMEN (SIN CAMBIO DE ESTADO) ---
    const handleGuardarDictamen = async () => {
        // Validaciones mínimas
        if (!datosDictamen.fecha) {
            toast.error("La fecha del dictamen es obligatoria.");
            return;
        }
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) {
            toast.warning("Se recomienda adjuntar el archivo del dictamen.");
        }

        setCargando(true);
        try {
            const archivosSubidos = await subirArchivosPendientes();

            // Construir comentario
            const comentario = `Dictamen Agregado\nFecha: ${formatearFecha(datosDictamen.fecha)}\nDetalles: ${datosDictamen.observaciones || 'Sin detalles adicionales'}\nArchivos adjuntos: ${archivosSubidos.map(a => a.nombre).join(', ')}`;

            // Usamos el ID del estado actual para NO avanzar
            // Aseguramos que obtenemos el ID correctamente
            const idEstadoActual = tramite.idEstadoActual || (tramite.estadoActual && tramite.estadoActual.idEstado);

            if (!idEstadoActual) {
                console.error("No se pudo determinar el estado actual del trámite", tramite);
                throw new Error("No se pudo identificar el estado actual para registrar el dictamen.");
            }

            const cambioEstadoData = {
                idEstado: idEstadoActual,
                comentario: comentario
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.success("Dictamen registrado correctamente. El expediente continúa en espera de sentencia.");

            // Reset parcial
            cancelarFormulario();
            cargarHistorial(); // Refrescamos para ver el nuevo comentario

        } catch (error) {
            console.error("Error al registrar dictamen:", error);
            toast.error("Error al registrar dictamen: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
            setSubiendoArchivos(false);
        }
    };

    // --- ACCIÓN 2: GUARDAR SENTENCIA (CAMBIO A ESTADO 19) ---
    const handleGuardarSentencia = async () => {
        // Validaciones
        if (!datosSentencia.resultado) {
            toast.error("Debe seleccionar un resultado (Favorable o Desfavorable).");
            return;
        }
        if (!datosSentencia.fecha) {
            toast.error("La fecha de la sentencia es obligatoria.");
            return;
        }
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) {
            toast.error("Debe adjuntar el archivo PDF de la sentencia.");
            return;
        }

        setCargando(true);
        try {
            const archivosSubidos = await subirArchivosPendientes();

            // Crear comentario
            const comentario = `Sentencia Judicial Registrada\nResultado: ${datosSentencia.resultado}\nFecha de Sentencia: ${formatearFecha(datosSentencia.fecha)}\nObservaciones: ${datosSentencia.observaciones || 'Sin observaciones'}\nArchivos adjuntos: ${archivosSubidos.map(a => a.nombre).join(', ')}`;

            // Cambiar a estado "Sentencia Judicial" (ID 19)
            const cambioEstadoData = {
                idEstado: 19,
                comentario: comentario
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.success("Sentencia Judicial cargada correctamente. El trámite ha finalizado la etapa judicial.");

            cancelarFormulario();
            if (onEstadoCambiado) await onEstadoCambiado();

        } catch (error) {
            console.error("Error al registrar sentencia:", error);
            toast.error("Error al registrar la sentencia: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
            setSubiendoArchivos(false);
        }
    };

    const cancelarFormulario = () => {
        setFormularioActivo(null);
        setArchivos([]);
        setDatosDictamen({ fecha: obtenerFechaActual(), observaciones: '' });
        setDatosSentencia({ resultado: '', fecha: obtenerFechaActual(), observaciones: '' });
    };

    const renderInputFile = () => (
        <div className="mt-4">
            <h6 className="fw-semibold text-secondary">Documentación Adjunta (PDF) <span className="text-danger">*</span></h6>
            {archivos.length > 0 && (
                <div className="list-group mb-3">
                    {archivos.map(archivo => (
                        <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                                <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                <span className="fw-semibold">{archivo.nombre}</span>
                                <small className="text-muted ms-2">({archivo.tamaño})</small>
                                <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                    {archivo.estado}
                                </span>
                            </div>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarArchivo(archivo.id)} title="Eliminar">
                                <DeleteIcon fontSize="small" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div
                className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <input type="file" ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" accept=".pdf,.PDF" />
                <CloudUploadIcon className="text-muted mb-2" />
                <p className="mb-0">Arrastra el archivo aquí o haz clic para seleccionar</p>
            </div>
        </div>
    );

    if (cargandoHistorial) {
        return <div className="card shadow-lg border-0 rounded-4 p-4 mb-4"><div className="alert alert-info">Cargando información...</div></div>;
    }

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">
                    <HourglassEmptyIcon className="me-2" />
                    Espera Dictamen Tribunal
                </h4>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Estado Actual:</strong>
                <p className="mb-0 mt-1">
                    Trámite judicializado. Se espera la presentación de dictámenes periciales o la sentencia final.
                </p>
            </div>

            {/* Información Pericia (solo lectura) */}
            {infoPericia && (
                <div className="mt-3 p-3 bg-light rounded-3 border mb-4">
                    <h6 className="fw-semibold text-secondary mb-3">Resumen de Antecedentes</h6>
                    <div className="row g-3">
                        {infoPericia.fechaRealizacion && (
                            <div className="col-md-6 d-flex align-items-center">
                                <CalendarMonthIcon className="text-primary me-2" />
                                <div><small className="text-muted d-block">Fecha Pericia Médica</small><strong className="text-dark">{infoPericia.fechaRealizacion}</strong></div>
                            </div>
                        )}
                        {infoAbogado && (
                            <div className="col-md-6 d-flex align-items-center">
                                <PersonIcon className="text-primary me-2" />
                                <div><small className="text-muted d-block">Abogado Responsable</small><strong className="text-dark">{infoAbogado}</strong></div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* SELECCIÓN DE ACCIÓN o FORMULARIO */}
            {!formularioActivo ? (
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                    <button
                        className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-4 py-3 fw-semibold"
                        onClick={() => setFormularioActivo('dictamen')}
                        disabled={cargando}
                    >
                        <ArticleIcon fontSize="medium" />
                        Registrar Dictamen
                    </button>
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-3 fw-semibold"
                        onClick={() => setFormularioActivo('sentencia')}
                        disabled={cargando}
                    >
                        <GavelIcon fontSize="medium" />
                        Cargar Sentencia Final
                    </button>
                </div>
            ) : (
                <div className="mt-4 animate__animated animate__fadeIn">
                    <div className="card shadow-sm border-0 rounded-4 p-4 bg-light">

                        {/* FORMULARIO DICTAMEN */}
                        {formularioActivo === 'dictamen' && (
                            <>
                                <h5 className="fw-semibold text-primary mb-3">Registrar Dictamen / Informe</h5>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Fecha de Presentación</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={datosDictamen.fecha}
                                            onChange={e => setDatosDictamen({ ...datosDictamen, fecha: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">Detalles del Dictamen</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={datosDictamen.observaciones}
                                            onChange={e => setDatosDictamen({ ...datosDictamen, observaciones: e.target.value })}
                                            placeholder="Describa el dictamen pericial o del tribunal..."
                                        />
                                    </div>
                                </div>
                                {renderInputFile()}
                                <div className="d-flex justify-content-end gap-3 mt-4">
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={cancelarFormulario} disabled={cargando}>Cancelar</button>
                                    <button className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2" onClick={handleGuardarDictamen} disabled={cargando || subiendoArchivos}>
                                        <UploadFileIcon fontSize="small" />
                                        {cargando ? 'Guardando...' : 'Guardar Dictamen'}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* FORMULARIO SENTENCIA */}
                        {formularioActivo === 'sentencia' && (
                            <>
                                <h5 className="fw-semibold text-primary mb-3">Cargar Sentencia Judicial</h5>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Resultado <span className="text-danger">*</span></label>
                                        <select
                                            className="form-select"
                                            value={datosSentencia.resultado}
                                            onChange={e => setDatosSentencia({ ...datosSentencia, resultado: e.target.value })}
                                            required
                                        >
                                            <option value="">Seleccionar...</option>
                                            <option value="Favorable">Favorable</option>
                                            <option value="Desfavorable">Desfavorable</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Fecha Sentencia <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={datosSentencia.fecha}
                                            onChange={e => setDatosSentencia({ ...datosSentencia, fecha: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">Observaciones</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={datosSentencia.observaciones}
                                            onChange={e => setDatosSentencia({ ...datosSentencia, observaciones: e.target.value })}
                                            placeholder="Detalles sobre el fallo..."
                                        />
                                    </div>
                                </div>
                                {renderInputFile()}
                                <div className="d-flex justify-content-end gap-3 mt-4">
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={cancelarFormulario} disabled={cargando}>Cancelar</button>
                                    <button className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2" onClick={handleGuardarSentencia} disabled={cargando || subiendoArchivos}>
                                        <CheckCircleIcon fontSize="small" />
                                        {cargando ? 'Procesando...' : 'Confirmar Sentencia'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default GestorEstadoEsperaDictamenTribunal;
